import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bell, AtSign, Calendar as CalendarIcon, Timer, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 66 — Smart Reminders   ·   Phase 68 — cross-device ack
 *
 * A single always-visible Bell button in the top nav that opens a curated
 * popup summarizing everything the recruiter should act on right now:
 *   • Unread @mentions tagging them
 *   • Interviews in the next 24 hours
 *   • Candidates stuck in Screening > 3 days
 *
 * Auto-open behavior (Phase 68): once per hour, the panel pops open
 * automatically on any page. The "last seen" timestamp is now persisted
 * server-side on the user doc (`last_reminders_ack_at`) so the cadence
 * syncs across devices. localStorage is used only as an offline fallback.
 */
const SNOOZE_KEY = "pw-reminders-lastseen";

export default function SmartRemindersBell() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const pollRef = useRef(null);

    const canUse = user && ["employer", "recruiter"].includes(user.role);

    const load = async () => {
        if (!canUse) return;
        try {
            const { data } = await api.get("/dashboard/reminders");
            setData(data);
        } catch { /* silent */ }
    };

    const ack = useCallback(async () => {
        try {
            const { data: d } = await api.post("/dashboard/reminders/ack");
            localStorage.setItem(SNOOZE_KEY, String(Date.now()));
            setData((prev) => prev ? { ...prev, last_ack_at: d?.last_ack_at } : prev);
        } catch {
            localStorage.setItem(SNOOZE_KEY, String(Date.now()));
        }
    }, []);

    useEffect(() => {
        if (!canUse) return () => {};
        load();
        pollRef.current = setInterval(load, 300000);
        // Cross-device auto-open: prefer server timestamp; fallback to local.
        const check = () => {
            const serverAckMs = data?.last_ack_at ? new Date(data.last_ack_at).getTime() : 0;
            const localAckMs = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
            const lastMs = Math.max(serverAckMs, localAckMs);
            const oneHourAgo = Date.now() - 3600000;
            if (lastMs < oneHourAgo && (data?.total || 0) > 0) {
                setOpen(true);
                ack();
            }
        };
        const t = setTimeout(check, 20000);
        const interval = setInterval(check, 300000);
        return () => { clearInterval(pollRef.current); clearTimeout(t); clearInterval(interval); };
    }, [canUse, data?.total, data?.last_ack_at, ack]);

    if (!canUse) return null;

    const total = data?.total || 0;

    const handleOpen = () => {
        setOpen(true);
        ack();
    };

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                className="relative p-1.5 border border-transparent hover:border-border transition-colors"
                title="Smart reminders"
                data-testid="reminders-bell"
            >
                <Bell className="w-4 h-4" />
                {total > 0 && (
                    <span
                        className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-rose-500 text-white text-[9px] font-mono font-bold uppercase tracking-widest inline-flex items-center justify-center"
                        data-testid="reminders-badge"
                    >
                        {total > 9 ? "9+" : total}
                    </span>
                )}
            </button>

            <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
                <DialogContent className="max-w-2xl p-0 rounded-none" data-testid="reminders-panel">
                    <div className="p-4 border-b border-border bg-secondary/40">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-ikb">Focus</div>
                        <DialogTitle asChild>
                            <h2 className="font-display font-bold text-lg">Smart reminders</h2>
                        </DialogTitle>
                        <DialogDescription className="sr-only">Items you should act on now</DialogDescription>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto">
                        {!data ? (
                            <div className="p-4 text-xs font-mono text-muted-foreground">Loading…</div>
                        ) : total === 0 ? (
                            <div className="p-8 text-center">
                                <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground">All caught up</div>
                                <div className="text-sm text-muted-foreground mt-2">No mentions, no interviews in the next 24 hours, no stalled candidates. Nicely done.</div>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                <ReminderSection
                                    label="You were mentioned"
                                    icon={AtSign}
                                    tint="rose"
                                    items={data.mentions}
                                    onClose={() => setOpen(false)}
                                    render={(m) => (
                                        <>
                                            <div className="text-sm">
                                                <span className="text-muted-foreground">by </span>
                                                <span className="font-medium">{m.by || "a teammate"}</span>
                                                {m.candidate_name && <>
                                                    <span className="text-muted-foreground"> on </span>
                                                    <span className="font-medium">{m.candidate_name}</span>
                                                </>}
                                            </div>
                                            {m.excerpt && (
                                                <blockquote className="mt-1.5 border-l-2 border-rose-300 pl-2.5 text-[13px] text-foreground/80 line-clamp-2">{m.excerpt}</blockquote>
                                            )}
                                        </>
                                    )}
                                />
                                <ReminderSection
                                    label="Interviews in the next 24 hours"
                                    icon={CalendarIcon}
                                    tint="violet"
                                    items={data.interviews}
                                    onClose={() => setOpen(false)}
                                    render={(m) => (
                                        <div className="text-sm">
                                            <span className="font-medium">{m.round_name}</span>
                                            {m.candidate_name && <span className="text-muted-foreground"> · <span className="text-foreground">{m.candidate_name}</span></span>}
                                            {m.scheduled_at && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{new Date(m.scheduled_at).toLocaleString()} · {m.mode || "TBD"}</div>}
                                            {m.meet_link && <a href={m.meet_link} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-ikb inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> Join</a>}
                                        </div>
                                    )}
                                />
                                <ReminderSection
                                    label="Stalled in Screening (>3 days)"
                                    icon={Timer}
                                    tint="amber"
                                    items={data.stalled}
                                    onClose={() => setOpen(false)}
                                    snoozable
                                    render={(m) => (
                                        <div className="text-sm">
                                            <span className="font-medium">{m.candidate_name || "Candidate"}</span>
                                            {m.job_title && <span className="text-muted-foreground"> · {m.job_title}</span>}
                                            {typeof m.match_score === "number" && (
                                                <span className="ml-2 text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5">AI {m.match_score}</span>
                                            )}
                                            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">Untouched since {m.stuck_since ? new Date(m.stuck_since).toLocaleDateString() : "—"}</div>
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

const TINT = {
    rose:   { icon: "text-rose-600",   bg: "bg-rose-50" },
    violet: { icon: "text-violet-600", bg: "bg-violet-50" },
    amber:  { icon: "text-amber-600",  bg: "bg-amber-50" },
};

function ReminderSection({ label, icon: Icon, tint, items, render, onClose, snoozable = false }) {
    const [snoozed, setSnoozed] = useState({}); // id -> true
    const [snoozeFor, setSnoozeFor] = useState(null); // id currently showing reason input
    const [reasonText, setReasonText] = useState("");
    const visible = items?.filter((m) => !snoozed[m.id]) || [];
    if (!visible.length) return null;
    const t = TINT[tint] || TINT.rose;

    const submitSnooze = async (id) => {
        try {
            await api.post("/ai-feedback", {
                feature: "urgency_task",
                action: "snooze",
                target_type: "application",
                target_id: String(id).replace(/^stall:/, ""),
                reason: reasonText.trim().slice(0, 300) || "no reason given",
            });
            setSnoozed((s) => ({ ...s, [id]: true }));
            setSnoozeFor(null);
            setReasonText("");
            toast.success("Snoozed — we'll skip it next time");
        } catch { toast.error("Couldn't snooze"); }
    };

    return (
        <section data-testid={`reminders-section-${tint}`}>
            <header className={`px-4 py-2 flex items-center gap-2 ${t.bg}`}>
                <Icon className={`w-3.5 h-3.5 ${t.icon}`} />
                <span className="text-[10px] font-mono uppercase tracking-widest">{label}</span>
                <span className="text-[10px] font-mono text-muted-foreground">· {visible.length}</span>
            </header>
            <ul className="divide-y divide-border">
                {visible.map((m) => (
                    <li key={m.id} className="relative group">
                        <Link
                            to={m.link}
                            onClick={onClose}
                            className="block px-4 py-3 pr-24 hover:bg-secondary/50"
                            data-testid={`reminder-${m.id}`}
                        >
                            {render(m)}
                        </Link>
                        {snoozable && snoozeFor !== m.id && (
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSnoozeFor(m.id); setReasonText(""); }}
                                className="absolute right-3 top-3 h-6 px-2 border border-border text-[10px] font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 hover:border-amber-500 hover:text-amber-700 transition-opacity bg-white"
                                title="Snooze — tell us why this isn't urgent"
                                data-testid={`reminder-snooze-${m.id}`}
                            >
                                Snooze
                            </button>
                        )}
                        {snoozable && snoozeFor === m.id && (
                            <div className="px-4 pb-3 -mt-2 flex items-center gap-1.5 bg-amber-50/50">
                                <input
                                    type="text"
                                    autoFocus
                                    value={reasonText}
                                    onChange={(e) => setReasonText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") submitSnooze(m.id); if (e.key === "Escape") setSnoozeFor(null); }}
                                    placeholder="Why isn't this urgent? (optional)"
                                    className="h-6 px-1.5 text-[11px] border border-border bg-white flex-1"
                                    data-testid={`reminder-snooze-reason-${m.id}`}
                                    maxLength={200}
                                />
                                <button
                                    type="button"
                                    onClick={() => submitSnooze(m.id)}
                                    className="h-6 px-2 border border-amber-500 text-amber-700 hover:bg-amber-50 text-[10px] font-mono uppercase tracking-widest"
                                    data-testid={`reminder-snooze-submit-${m.id}`}
                                >Snooze</button>
                                <button
                                    type="button"
                                    onClick={() => setSnoozeFor(null)}
                                    className="h-6 w-6 grid place-items-center border border-border hover:border-foreground"
                                    aria-label="Cancel snooze"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}
